/**
 * Modal Field Adapter for Manufacturers
 * Transforms Manufacturer Field Registry into Modal field configurations
 */

import { FieldConfig } from '../../../components/modals/ModalFieldRenderer';
import {
  Manufacturer,
  ProductSummary,
} from '../../../services/api/domains/manufacturers/types';
import { MANUFACTURER_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { validationHelpers } from '../../../components/modals/utils/validation';
import { formatDateTime } from '../../../utils/dateConversion';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';
import { FacilitySummary } from '../../../services/api/domains/products/types';

/**
 * Convert a basic field definition to modal field config
 */
function createStandardField(
  field: BaseFieldDefinition
): FieldConfig<Manufacturer> {
  // Determine field type
  let fieldType: 'text' | 'textarea' | 'number' | 'date' | 'readonly' = 'text';

  if (field.isReadOnly) {
    fieldType = 'readonly';
  } else if (field.renderConfig?.rows && field.renderConfig.rows > 1) {
    fieldType = 'textarea';
  } else if (field.dataType === 'number') {
    fieldType = 'number';
  } else if (field.dataType === 'date') {
    fieldType = 'date';
  }

  const config: FieldConfig<Manufacturer> = {
    key: field.key as keyof Manufacturer,
    label: field.label,
    type: fieldType,
    getValue: (m: Manufacturer) => {
      const value = m[field.key as keyof Manufacturer];
      return value || '';
    },
    setValue: field.isReadOnly
      ? undefined
      : (m: Manufacturer, val: unknown) => ({
          ...m,
          [field.key]: val || undefined,
        }),
    isAlwaysDisabled: field.isReadOnly,
    isRequired: field.isRequired,
    tooltipText: field.description,
    rows: field.renderConfig?.rows,
  };

  // Add formatter for date fields
  if (field.dataType === 'date' && field.isReadOnly) {
    config.formatter = formatDateTime;
  }

  // Add validation for required fields
  if (field.isRequired && field.validation) {
    const validators = [];
    validators.push(validationHelpers.required);
    if (field.validation.minLength) {
      validators.push(validationHelpers.minLength(field.validation.minLength));
    }
    config.validator = validationHelpers.combine(...validators);
  }

  return config;
}

/**
 * Create async multiselect field for facilities
 * Uses API-based pagination and search
 */
function createFacilitiesField(
  field: BaseFieldDefinition
): FieldConfig<Manufacturer> {
  const { relationConfig } = field;
  if (!relationConfig) {
    throw new Error(
      `Field ${String(field.key)} is marked as relation but missing relationConfig`
    );
  }

  const { idField } = relationConfig;

  // Get context-aware config for modal (uses ID field for database storage)
  const contextConfig = getContextAwareConfigFromRegistry(
    MANUFACTURER_FIELDS_REGISTRY,
    field.key as string,
    'modal' // ✅ Modal context uses ID for database storage
  );

  if (!contextConfig) {
    throw new Error(
      `Field ${String(field.key)} is missing async select configuration`
    );
  }

  // Convert to standard format
  const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

  return {
    key: field.key as keyof Manufacturer,
    label: field.label,
    type: 'async_multiselect',
    getValue: (m: Manufacturer) => {
      const facilities = m.facilities;
      if (!Array.isArray(facilities)) return [];
      return facilities.map(
        f => f[idField as keyof typeof f]?.toString() || ''
      );
    },
    setValue: (m: Manufacturer, val: unknown) => {
      const selectedIds = Array.isArray(val) ? val : [];
      const facilities: FacilitySummary[] = selectedIds.map(id => ({
        facility_id: parseInt(id, 10),
        facility_name: '',
      }));

      return { ...m, facilities };
    },
    apiEndpoint: asyncConfig.apiEndpoint,
    searchField: asyncConfig.searchField,
    valueField: asyncConfig.valueField,
    labelField: asyncConfig.labelField,
    isRequired: field.isRequired,
    tooltipText: field.description,
  };
}

/**
 * Create async multiselect field for products
 * Uses API-based pagination and search
 */
function createProductsField(
  field: BaseFieldDefinition
): FieldConfig<Manufacturer> {
  const { relationConfig } = field;
  if (!relationConfig) {
    throw new Error(
      `Field ${String(field.key)} is marked as relation but missing relationConfig`
    );
  }

  const { idField } = relationConfig;

  // Get context-aware config for modal (uses ID field for database storage)
  const contextConfig = getContextAwareConfigFromRegistry(
    MANUFACTURER_FIELDS_REGISTRY,
    field.key as string,
    'modal' // ✅ Modal context uses ID for database storage
  );

  if (!contextConfig) {
    throw new Error(
      `Field ${String(field.key)} is missing async select configuration`
    );
  }

  // Convert to standard format
  const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

  return {
    key: field.key as keyof Manufacturer,
    label: field.label,
    type: 'async_multiselect',
    getValue: (m: Manufacturer) => {
      const products = m.products;
      if (!Array.isArray(products)) return [];
      return products.map(p => p[idField as keyof typeof p]?.toString() || '');
    },
    setValue: (m: Manufacturer, val: unknown) => {
      const selectedIds = Array.isArray(val) ? val : [];
      const products: ProductSummary[] = selectedIds.map(id => ({
        product_id: parseInt(id, 10),
        product_name: '',
        preferred_product_name: null,
        manufacturing_status: null,
      }));

      return { ...m, products };
    },
    apiEndpoint: asyncConfig.apiEndpoint,
    searchField: asyncConfig.searchField,
    valueField: asyncConfig.valueField,
    labelField: asyncConfig.labelField,
    isRequired: field.isRequired,
    tooltipText: field.description,
  };
}

/**
 * Convert Manufacturer Field Registry to Modal field configurations
 * @returns Array of FieldConfig for Modal
 */
export function toModalFields(): FieldConfig<Manufacturer>[] {
  const fields: FieldConfig<Manufacturer>[] = [];

  for (const field of MANUFACTURER_FIELDS_REGISTRY) {
    // Skip computed fields (not editable)
    if (field.isComputed) {
      continue;
    }

    // Handle facilities async multiselect
    if (field.key === 'facilities' && field.isRelation) {
      fields.push(createFacilitiesField(field));
      continue;
    }

    // Handle products async multiselect
    if (field.key === 'products' && field.isRelation) {
      fields.push(createProductsField(field));
      continue;
    }

    // Handle standard fields (string, number, date, readonly)
    if (field.dataType !== 'array' && field.dataType !== 'object') {
      fields.push(createStandardField(field));
    }
  }

  return fields;
}

/**
 * Get always disabled field names for manufacturers
 */
export function getAlwaysDisabledFields(): string[] {
  return MANUFACTURER_FIELDS_REGISTRY.filter(f => f.isReadOnly).map(
    f => f.key as string
  );
}

/**
 * Get manufacturer ID field for display in view mode
 */
export function getManufacturerIdField(): FieldConfig<Manufacturer> {
  return {
    key: 'manufacturer_id',
    label: 'Manufacturer ID',
    type: 'text',
    getValue: m => m.manufacturer_id?.toString() || '',
    setValue: (m, val) => ({
      ...m,
      manufacturer_id: parseInt(val as string) || 0,
    }),
    isRequired: false,
  };
}
